<template>
  <ValidationObserver v-slot="{ handleSubmit }">
    <form
      action="#"
      @submit.prevent="handleSubmit(validateForm)"
      novalidate
      ref="form"
    >
      <Alert
        color="success"
        v-if="showSuccess"
        aria-live="polite"
        style="margin-bottom: 1rem"
      >
        <CheckIcon slot="icon" />
        {{ successMessage }}
      </Alert>
      <Alert
        color="error"
        v-else-if="errorMessage"
        aria-live="assertive"
        style="margin-bottom: 1rem"
      >
        <ErrorIcon slot="icon" />
        {{ errorMessage }}
      </Alert>

      <template v-if="!showSuccess">
        <ValidationProvider
          v-slot="{ errors, ariaInput, ariaMsg }"
          rules="required"
          name="First Name"
        >
          <FormGroup>
            <Label for="first-name">First Name *</Label>
            <Input
              id="first-name"
              v-model="firstName"
              placeholder="John"
              v-bind="ariaInput"
              name="first_name"
            />
            <FormGroupHelp color="error" v-bind="ariaMsg">{{
              errors[0]
            }}</FormGroupHelp>
          </FormGroup>
        </ValidationProvider>

        <ValidationProvider
          v-slot="{ errors, ariaInput, ariaMsg }"
          rules="required"
          name="Last Name"
        >
          <FormGroup>
            <Label for="last-name">Last Name *</Label>
            <Input
              id="last-name"
              v-model="lastName"
              placeholder="Smith"
              v-bind="ariaInput"
              name="last_name"
            />
            <FormGroupHelp color="error" v-bind="ariaMsg">{{
              errors[0]
            }}</FormGroupHelp>
          </FormGroup>
        </ValidationProvider>

        <ValidationProvider
          v-slot="{ errors, ariaInput, ariaMsg }"
          rules="required|email"
          name="Business Email"
        >
          <FormGroup>
            <Label for="email">Business Email *</Label>
            <Input
              id="email"
              v-model="email"
              type="email"
              placeholder="johnsmith@acmestudio.com"
              v-bind="ariaInput"
              name="email"
            />
            <FormGroupHelp color="error" v-bind="ariaMsg">{{
              errors[0]
            }}</FormGroupHelp>
          </FormGroup>
        </ValidationProvider>

        <ValidationProvider
          v-slot="{ errors, ariaInput, ariaMsg }"
          rules="required"
          name="Company Name"
        >
          <FormGroup>
            <Label for="company-name">Company Name *</Label>
            <Input
              id="company-name"
              v-model="companyName"
              placeholder="Acme Studios"
              v-bind="ariaInput"
              name="company_name"
            />
            <FormGroupHelp color="error" v-bind="ariaMsg">{{
              errors[0]
            }}</FormGroupHelp>
          </FormGroup>
        </ValidationProvider>

        <ValidationProvider
          v-slot="{ errors, ariaInput, ariaMsg }"
          rules="required"
          name="Country"
        >
          <FormGroup>
            <Label for="country">Country *</Label>
            <CountrySelect
              id="country"
              v-model="country"
              v-bind="ariaInput"
              name="country"
            />
            <FormGroupHelp color="error" v-bind="ariaMsg">{{
              errors[0]
            }}</FormGroupHelp>
          </FormGroup>
        </ValidationProvider>

        <ValidationProvider
          v-slot="{ errors, ariaInput, ariaMsg }"
          rules="required"
          name="Company Size"
        >
          <FormGroup>
            <Label for="company-size">Company Size *</Label>
            <Select
              id="company-size"
              v-model="companySize"
              v-bind="ariaInput"
              name="company_size"
            >
              <option :value="null">Select an option</option>
              <option value="1">Myself only</option>
              <option value="2-10">2-10</option>
              <option value="11-50">11-50</option>
              <option value="51-100">51-100</option>
              <option value="101-500">101-500</option>
              <option value="501-1000">501-1000</option>
              <option value="1001-5000">1001-5000</option>
              <option value="5001-10000">5001-10000</option>
              <option value="10001+">10001+</option>
            </Select>
            <FormGroupHelp color="error" v-bind="ariaMsg">{{
              errors[0]
            }}</FormGroupHelp>
          </FormGroup>
        </ValidationProvider>

        <FormGroup>
          <Label for="notes">Notes</Label>
          <Input id="notes" v-model="notes" component="textarea" name="notes" />
        </FormGroup>

        <div class="hide">
          <input type="text" name="phone" autocomplete="off" v-model="phone" />
          <input type="hidden" name="token" v-model="captcha" />
        </div>

        <VueHcaptcha
          ref="invisibleRecaptcha"
          sitekey="5bbad349-e41e-4564-b8da-6dde650962dc"
          @verify="captchaResponse"
          @expired="captcha = null"
          size="invisible"
        />

        <Button
          type="submit"
          style="width: 100%"
          size="large"
          :loading="isCaptching || isSending"
        >
          Request a Demo
        </Button>
      </template>
    </form>
  </ValidationObserver>
</template>

<script>
import {
  FormGroup,
  FormGroupHelp,
  Input,
  Select,
  Label,
  Button,
  Alert,
  ErrorIcon,
  CheckIcon,
} from '@campsite-bio/component-lib';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { nextTick, ref, useContext } from '@nuxtjs/composition-api';
import VueHcaptcha from '@hcaptcha/vue-hcaptcha';

import CountrySelect from '@/components/forms/country-select';

export default {
  components: {
    ValidationObserver,
    ValidationProvider,
    FormGroup,
    FormGroupHelp,
    Input,
    Select,
    Label,
    Button,
    CountrySelect,
    VueHcaptcha,
    Alert,
    ErrorIcon,
    CheckIcon,
  },

  setup() {
    const firstName = ref(null);
    const lastName = ref(null);
    const email = ref(null);
    const companyName = ref(null);
    const companySize = ref(null);
    const country = ref(null);
    const notes = ref(null);
    const invisibleRecaptcha = ref(null);
    const form = ref(null);
    const phone = ref(null);
    const captcha = ref(false);
    const isCaptching = ref(false);
    const isSending = ref(false);
    const showSuccess = ref(false);
    const successMessage = ref(null);
    const errorMessage = ref(null);

    const { $axios, $sentryReady, isDev, $config } = useContext();

    async function captchaResponse(response) {
      isCaptching.value = false;
      captcha.value = response;
      await nextTick();
      send();
    }

    function validateForm() {
      if (isDev) {
        send();
        return;
      }

      if (isCaptching.value) return;

      // verify human
      invisibleRecaptcha.value.execute();
      isCaptching.value = true;
    }

    async function send() {
      if (isSending.value) return;

      isSending.value = true;
      try {
        const formData = new FormData(form.value);

        const { data } = await $axios.request({
          method: 'post',
          url: `${$config.apiBaseUrl}/marketing/demo-request`,
          responseType: 'json',
          data: formData,
        });

        showSuccess.value = true;
        successMessage.value = data.message;

        if (window.fbq) {
          window.fbq('track', 'Lead');
        }
      } catch (e) {
        invisibleRecaptcha.value.reset();
        console.error(e);
        // The API says why it refused the form ("Invalid email address.", a failed captcha);
        // show that so the visitor can fix it, not only "try again" (CAM-722).
        errorMessage.value =
          (e.response && e.response.data && e.response.data.error_message) ||
          'There was an error when trying to contact us. Please try again soon.';
        // $sentry from setup is the lazy-load mock, which throws once the SDK has
        // loaded; $sentryReady resolves with the loaded SDK (CAM-673).
        $sentryReady().then((sentry) => sentry.captureException(e));
      } finally {
        isSending.value = false;
      }
    }

    return {
      firstName,
      lastName,
      email,
      companyName,
      companySize,
      country,
      notes,
      send,
      validateForm,
      captchaResponse,
      invisibleRecaptcha,
      phone,
      captcha,
      isCaptching,
      isSending,
      showSuccess,
      form,
      successMessage,
      errorMessage,
    };
  },
};
</script>

<style lang="scss" scoped></style>
